import {
  getDocs,
  getDoc,
  doc,
  updateDoc,
  deleteDoc,
  orderBy,
  query,
  where,
  setDoc,
  collection,
  writeBatch,
  getCountFromServer,
} from 'firebase/firestore'
import {
  ref as storageRef,
  getDownloadURL,
  uploadString,
} from 'firebase/storage'
import { getUnix } from '@/libs/date-format'
import { db } from '@/libs/firebase'
import { uniqBy } from 'lodash'
import { constructQuery } from '@core/utils/utils'
import { RevisionEventType } from '@core/revisions/revisions'
import Vue from 'vue'
import { nextTick } from '@vue/composition-api'
import store from '@/store'
import i18n from '@/libs/i18n'
import { getTenantContextInstance as tenantCtx } from '@/plugins/tenant'
import router from '@/router'
import { allowed, FEATURES } from '@/auth'
import { sanitizeLanguageMap } from '@/libs/content-translator/sanitize'

export default {
  namespaced: true,
  state: {
    list: [],
    current: {
      data: {},
      snapshot: {},
    },
    count: null,
    languages: {},
    listRelated: [],
    filteredList: [],
    translations: {},
    listView: localStorage.getItem('novti-pages-list-view') || 'grid',
    thumbnailPlaceholder: process.env.VUE_APP_PAGES_THUMBNAIL_PLACEHOLDER,
  },
  getters: {
    getCurrent: state => state.current,
    getListView: state => state.listView,
    getTranslations: state => state.translations,
    getThumbnailPlaceholder: state => state.thumbnailPlaceholder,
    getAll: state => state.list.map(docRef => ({ ...docRef.data(), id: docRef.id })),
    getFilteredList: state => state.list.map(docRef => ({ ...docRef.data(), id: docRef.id })),
    getById: state => id => state.list.map(docRef => ({ snapshot: docRef, data: { ...docRef.data(), id: docRef.id } })).filter(el => !el.data.deletedAt).find(item => item.snapshot.id === id),
    getRelated: state => state.listRelated.map(docRef => ({ ...docRef.data(), id: docRef.id })).map(({ schema, ...item }) => item).filter(el => !el.deletedAt),
    getCount: state => state.count,
  },
  mutations: {
    UPDATE_LIST(state, payload) {
      const { docs, pagination } = payload

      state.list = (!pagination) ? docs : uniqBy([...state.list, ...docs], 'id')
    },
    UPDATE_LIST_RELATED(state, payload) {
      state.listRelated = payload
    },
    UPDATE_FILTERED_LIST(state, payload) {
      state.list = payload
    },
    UPDATE_TRANSLATIONS(state, payload) {
      state.translations = payload
    },
    SET_CURRENT(state, payload) {
      state.current = {
        snapshot: payload,
        data: { ...payload.data(), id: payload.id },
      }
    },
    UPDATE_LIST_VIEW(state, payload) {
      localStorage.setItem('novti-pages-list-view', payload)
      state.listView = payload
    },
    SET_DEFAULT_LANGUAGE(state, payload) {
      Vue.set(state.current.data, 'language', payload)
    },
    SET_SUPPORTED_LANGUAGES(state, payload) {
      Vue.set(state.current.data, 'supportedLanguages', payload)
    },
    SET_COUNT(state, payload) {
      state.count = payload
    },
  },
  actions: {
    /**
     * Creates a document by the given payload
     *
     * @param {Object} payload
     *
     * @returns {Promise}
     */
    create({ }, payload) {
      return new Promise((resolve, reject) => {
        if (!allowed(FEATURES.PAGES_QUOTA)) {
          reject()

          store.dispatch('notify', {
            body: i18n.t('You have reached your {title} limit', { title: i18n.t('page limit') }),
            variant: 'danger',
          })

          return
        }

        const { pages } = tenantCtx()

        // This is required if we want to filter on `deletedAt == null`, as per Firestore query limitations
        // eslint-disable-next-line no-param-reassign
        payload.deletedAt = null
        payload.published = false

        const docRef = doc(pages)

        setDoc(docRef, payload)
          .then(() => {
            store.dispatch('revisions/create', {
              event: RevisionEventType.CREATE_PAGES,
              id: docRef.id,
              newValue: payload,
              previousValue: {},
            })

            store.dispatch('notify', { title: i18n.t('Great!'), body: i18n.t('A new {title} has been created 🚀', { title: i18n.t('page') }) })
            
            resolve(docRef)
          })
          .catch(error => {
            store.dispatch('notify', {
              body: i18n.t('Something went wrong creating the {title}', { title: i18n.t('page') }),
              variant: 'danger',
            })

            reject(error)
          })
      })
    },

    /**
     * Fetches all
     *
     * @param {Object}
     * @param {Object} queryOptions
     *
     * @returns {Promise}
     */
    fetchAll({ commit }, payload = {}) {
      return new Promise((resolve, reject) => {
        const { pages } = tenantCtx()

        const { queryOptions, pagination = false } = payload

        const q = constructQuery(pages, 'createdAt', {
          ...queryOptions,
          where: [where('deletedAt', '==', null)],
        })

        getDocs(q)
          .then(querySnapshot => {
            commit('UPDATE_LIST', { docs: querySnapshot.docs, pagination })
            resolve(querySnapshot.docs)
          })
          .catch(error => {
            store.dispatch('notify', {
              body: i18n.t('Something went wrong retrieving the {title}', { title: i18n.t('pages') }),
              variant: 'danger',
            })

            reject(error)
          })
      })
    },

    /**
     * Fetches document count
     *
     * @param {Object} context - Vuex context
     *
     * @returns {Promise<number>} - Count of matching documents
     */
    fetchCount({ commit }) {
      return new Promise(async (resolve, reject) => {
        try {
          const { pages } = tenantCtx()

          const q = query(
            pages,
            where('deletedAt', '==', null),
          )

          const snapshot = await getCountFromServer(q)
          const { count } = snapshot.data()

          commit('SET_COUNT', count)
          resolve(count)
        } catch (error) {
          store.dispatch('notify', {
            body: i18n.t('Something went wrong retrieving the {title}', { title: i18n.t('pages') }),
            variant: 'danger',
          })
          reject(error)
        }
      })
    },

    /**
     * Fetch all the translations by the given id
     *
     * @param {Object} obj
     * @param {Object} obj.commit
     * @param {string} id
     *
     * @returns {Promise}
     */
    fetchAllTranslations({ commit }, id) {
      return new Promise((resolve, reject) => {
        const { pages } = tenantCtx()

        getDocs(collection(pages, id, 'translations')).then(querySnapshot => {
          // Blank values are dropped so they are treated as "not translated".
          const textContentsPerLanguage = querySnapshot.docs.reduce((acc, curr) => {
            acc[curr.id] = sanitizeLanguageMap(curr.data())

            return acc
          }, {})

          commit('UPDATE_TRANSLATIONS', textContentsPerLanguage)
          resolve(textContentsPerLanguage)
        }).catch(error => {
          store.dispatch('notify', {
            body: i18n.t('Something went wrong retrieving the {title}', { title: i18n.t('translations') }),
            variant: 'danger',
          })

          reject(error)
        })
      })
    },

    /**
     * Fetches on search query
     *
     * @param {Object}
     * @param {Object} queryOptions
     *
     * @returns {Promise}
     */
    fetchFiltered({ commit }, queryOptions) {
      return new Promise((resolve, reject) => {
        const { pages } = tenantCtx()

        const q = constructQuery(pages, 'createdAt', {
          ...queryOptions,
          where: [...queryOptions.where, where('deletedAt', '==', null)],
        })

        getDocs(q)
          .then(querySnapshot => {
            commit('UPDATE_FILTERED_LIST', querySnapshot.docs)
            resolve(querySnapshot.docs)
          })
          .catch(error => {
            store.dispatch('notify', {
              body: i18n.t('Something went wrong retrieving the {title}', { title: i18n.t('pages') }),
              variant: 'danger',
            })

            reject(error)
          })
      })
    },

    /**
     * Fetches a document by the given ID
     *
     * @param {string} id The document ID
     *
     * @returns {Promise} Retrieves all fields in the document as an Object. Returns undefined if the document doesn't exist.
     */
    fetchById({ }, id) {
      return new Promise((resolve, reject) => {
        const { pages } = tenantCtx()
        const docRef = doc(pages, id)

        getDoc(docRef)
          .then(docSnapshot => {
            if (!docSnapshot.exists()) {
              reject(new Error('404'))
            }

            if (docSnapshot.data().deletedAt) {
              reject(new Error('404'))
            }

            resolve(docSnapshot)
          })
          .catch(error => {
            store.dispatch('notify', {
              body: i18n.t('Something went wrong retrieving the {title}', { title: i18n.t('page') }),
              variant: 'danger',
            })

            reject(error)
          })
      })
    },

    /**
     * Fetches a document by the current item ID
     *
     * @param {string} id
     */
    fetchCurrent({ dispatch, commit }, id) {
      return new Promise((resolve, reject) => {
        dispatch('fetchById', id)
          .then(res => {
            commit('SET_CURRENT', res)
            resolve(res)
          })
          .catch(error => reject(error))
      })
    },

    /**
     * Updates the published flag for a page by id (list / bulk actions).
     * Does not require `state.current` to be loaded.
     *
     * @param {Object} payload
     * @param {string} payload.id
     * @param {boolean} payload.published
     * @param {Object} [payload.previous] - Previous page data for the revision
     *
     * @returns {Promise<boolean>}
     */
    updatePublished({ }, { id, published, previous = {} }) {
      return new Promise(async (resolve, reject) => {
        try {
          const { pages } = tenantCtx()
          const docRef = doc(pages, id)
          const updatedAt = getUnix()

          await updateDoc(docRef, {
            published,
            updatedAt,
          })

          store.dispatch('revisions/create', {
            event: RevisionEventType.UPDATE_PAGES,
            id,
            newValue: {
              published,
              updatedAt,
            },
            previousValue: {
              ...previous,
              recipes: null,
            },
          })

          resolve(true)
        } catch (error) {
          store.dispatch('notify', {
            body: i18n.t('Something went wrong updating the {title}', { title: i18n.t('page') }),
            variant: 'danger',
          })

          reject(error)
        }
      })
    },

    /**
     * Updates the current document ref by the given payload
     *
     * @param {Object} obj
     * @param {Object} obj.dispatch
     * @param {Object} obj.state
     * @param {Object} payload
     *
     * @returns {Promise}
     */
    updateCurrent({ dispatch, state }, payload) {
      return new Promise(async (resolve, reject) => {
        try {
          const newValue = {
            ...state.current.data,
            ...payload,
          }

          await updateDoc(state.current.snapshot.ref, {
            ...newValue,
            updatedAt: getUnix(),
          })

          const { id } = state.current.snapshot

          handlePageUpdateRevision(state, newValue, id)

          store.dispatch('notify', { body: i18n.t('{title} has been updated', { title: i18n.t('Page') }) })

          nextTick(() => {
            dispatch('fetchCurrent', id) // Update the current data as well
            resolve(true)
          })
        } catch (error) {
          store.dispatch('notify', {
            body: i18n.t('Something went wrong updating the {title}', { title: i18n.t('page') }),
            variant: 'danger',
          })

          reject(error)
        }
      })
    },

    /**
     * Stores the languages of the page: the language it is written in and the
     * languages it is translated into.
     *
     * Written on its own, so the page recipe and the revisions are left alone.
     *
     * @param {Object} obj
     * @param {Function} obj.commit
     * @param {Object} obj.state
     * @param {Object} payload
     * @param {string} [payload.language] The language the page is written in.
     * @param {string[]} [payload.supportedLanguages] The languages of the page.
     *
     * @returns {Promise<boolean>}
     */
    async updateTranslationSettings({ commit, state }, { language, supportedLanguages } = {}) {
      const changes = {}

      if (language !== undefined) {
        changes.language = language
        commit('SET_DEFAULT_LANGUAGE', language)
      }

      if (supportedLanguages !== undefined) {
        changes.supportedLanguages = supportedLanguages
        commit('SET_SUPPORTED_LANGUAGES', supportedLanguages)
      }

      if (Object.keys(changes).length === 0) {
        return true
      }

      try {
        await updateDoc(state.current.snapshot.ref, changes)

        return true
      } catch (error) {
        store.dispatch('notify', {
          body: i18n.t('Something went wrong updating the {title}', { title: i18n.t('page') }),
          variant: 'danger',
        })

        throw error
      }
    },

    /**
     * Removes a language from the page. `storePageTranslations` publishes an
     * empty file for the removed document, so the published page stops reading
     * the stale translations.
     *
     * @param {Object} obj
     * @param {Object} obj.state
     * @param {Object} obj.commit
     * @param {string} language The language to remove.
     *
     * @returns {Promise<boolean>}
     */
    async deleteTranslationLanguage({ state, commit }, language) {
      const { pages } = tenantCtx()

      try {
        await deleteDoc(doc(collection(pages, state.current.snapshot.id, 'translations'), language))

        const translations = { ...state.translations }

        delete translations[language]
        commit('UPDATE_TRANSLATIONS', translations)

        return true
      } catch (error) {
        store.dispatch('notify', {
          body: i18n.t('Something went wrong updating the {title}', { title: i18n.t('translations') }),
          variant: 'danger',
        })

        throw error
      }
    },

    /**
     * Deletes the stored translations of the given languages in one go.
     *
     * Used when the language the page is written in changes: the page texts are
     * still the same, so every translation that was made against the previous
     * language describes the wrong language from now on. `storePageTranslations`
     * publishes an empty file for each removed document, so the published page
     * falls back to its own texts.
     *
     * @param {Object} obj
     * @param {Object} obj.state
     * @param {Object} obj.commit
     * @param {Object} payload
     * @param {string} payload.id The page ID.
     * @param {string[]} payload.languages The languages to forget.
     *
     * @returns {Promise<boolean>}
     */
    async resetPageTranslations({ state, commit }, { id, languages = [] } = {}) {
      const { pages } = tenantCtx()
      const codes = (languages || []).filter(Boolean)

      try {
        if (codes.length) {
          const batch = writeBatch(db)
          const translationsCollection = collection(pages, id, 'translations')

          codes.forEach(language => {
            batch.delete(doc(translationsCollection, language))
          })

          await batch.commit()
        }

        const translations = { ...state.translations }

        codes.forEach(language => { delete translations[language] })
        commit('UPDATE_TRANSLATIONS', translations)

        return true
      } catch (error) {
        store.dispatch('notify', {
          body: i18n.t('Something went wrong updating the {title}', { title: i18n.t('translations') }),
          variant: 'danger',
        })

        throw error
      }
    },

    /**
     * Update the translations
     *
     * @param {Object} payload
     *
     * @returns {Promise}
     */
    async updateTranslations({ }, payload) {
      const { pages } = tenantCtx()

      try {
        const { id, translations = {} } = payload
        const languages = Object.keys(translations)

        // Nothing to store, just resolve
        if (languages.length === 0) {
          return true
        }

        // Lets open a batch write call against Firestore
        const batch = writeBatch(db)
        const translationsCollection = collection(pages, id, 'translations')

        languages.forEach(language => {
          batch.set(doc(translationsCollection, language), sanitizeLanguageMap(translations[language]))
        })

        await batch.commit()

        return true
      } catch (error) {
        console.error('Error updating page translations:', error)

        store.dispatch('notify', {
          body: i18n.t('Something went wrong updating the {title}', { title: i18n.t('translations') }),
          variant: 'danger',
        })

        throw error
      }
    },

    /**
     * Soft deletes a document
     *
     * @param {Object} payload
     *
     * @returns {Promise}
     */
    softDelete({ }, payload) {
      return new Promise((resolve, reject) => {
        const { pages } = tenantCtx()
        const docRef = doc(pages, payload.id)

        updateDoc(docRef, {
          deletedAt: getUnix(),
        })
          .then(() => {
            store.dispatch('revisions/create', {
              event: RevisionEventType.DELETE_PAGES,
              id: payload.id,
              newValue: {},
              previousValue: payload,
            })

            resolve(true)
          })
          .catch(error => {
            reject(error)
          })
      })
    },

    /**
     * Duplicates a document
     *
     * @param {Object}
     * @param {string} id The id of the document to duplicate
     *
     * @returns {Promise}
     */
    duplicate({ }, id) {
      return new Promise(async (resolve, reject) => {
        if (!allowed(FEATURES.PAGES_QUOTA)) {
          reject()

          store.dispatch('notify', {
            body: i18n.t('You have reached your {title} limit', { title: i18n.t('page limit') }),
            variant: 'danger',
          })

          return
        }

        try {
          const { pages } = tenantCtx()
          const docRef = doc(pages, id)
          const timestamp = getUnix()
          const randomNumber = Math.floor(Math.random() * 10)
          const docSnapshot = await getDoc(docRef)
          const docSnapshotData = docSnapshot.data()
          const newDocRef = doc(pages)

          await store.dispatch('tenant/fetchFeatureFlags')
          const featureFlags = store.getters['tenant/getFeatureFlags'] || {}
          const usesCloudStorageBackend = featureFlags.PAGE_STORAGE_BACKEND === 'google-cloud-storage'

          const duplicateSlug = `${docSnapshot.data().slug}-${timestamp}${randomNumber}`

          const data = {
            ...docSnapshotData,
            id: newDocRef.id,
            slug: duplicateSlug,
            // Set all timestamp properties.
            createdAt: timestamp,
            updatedAt: timestamp,
            deletedAt: null,
            // Set published false to avoid publishing the duplicate
            published: false,
          }

          // Recipe data lives in Cloud Storage for this backend. Omitting stale Firestore
          // recipes prevents storePageRecipe from overwriting the copied storage assets.
          if (usesCloudStorageBackend) {
            delete data.recipes
          }

          /**
           * If the page is using Cloud Storage as backend, copy its assets
           * (data.json, index.html, styles.css, translations/*.json) to the
           * new page id folder and persist the storageVersion from data.json metadata.
           */
          try {
            const { pagesStorage } = tenantCtx()

            if (pagesStorage) {
              const originalPageRef = storageRef(pagesStorage, id)
              const newPageRef = storageRef(pagesStorage, newDocRef.id)

              const copyFile = async (fileName, contentType) => {
                try {
                  const fileRef = storageRef(originalPageRef, fileName)
                  const url = await getDownloadURL(fileRef)
                  const response = await fetch(url)

                  if (!response.ok) {
                    throw new Error(`HTTP error ${response.status}`)
                  }

                  const content = await response.text()

                  const targetRef = storageRef(newPageRef, fileName)

                  return await uploadString(targetRef, content, 'raw', {
                    contentType,
                  })
                } catch (error) {
                  // Expected for pages that do not yet have Cloud Storage assets
                  if (
                    error.code === 'storage/object-not-found' ||
                    error.code === 'storage/not-found'
                  ) {
                    return null
                  }

                  console.error(`Error copying page asset "${fileName}" in storage:`, error)

                  return null
                }
              }

              const dataJsonResult = await copyFile('data.json', 'application/json')

              // Copy HTML and CSS if present
              await copyFile('index.html', 'text/html')
              await copyFile('styles.css', 'text/css')

              // Published pages read translations from Cloud Storage. Copy them
              // with the other assets so the duplicate does not depend on the
              // Cloud Function rewriting them after the Firestore docs land.
              const translationLanguages = [...new Set([
                ...(docSnapshotData?.supportedLanguages || []),
                docSnapshotData?.language,
              ].filter(Boolean))]

              await Promise.all(translationLanguages.map(language => (
                copyFile(`translations/${language}.json`, 'application/json')
              )))

              // Persist storageVersion metadata from data.json (if we have it)
              const { metadata } = dataJsonResult || {}

              if (metadata) {
                data.storageVersion = {
                  md5Hash: metadata.md5Hash,
                  generation: metadata.generation,
                }
              }
            }
          } catch (storageError) {
            // Log but do not fail the duplication if storage copy fails
            console.error('Error duplicating page assets in storage:', storageError)
          }

          await setDoc(newDocRef, data)

          const translationsCollection = collection(pages, id, 'translations')
          const translations = await getDocs(translationsCollection)

          if (!translations.empty) {
            const newTranslationsCollection = collection(pages, newDocRef.id, 'translations')
            const batch = writeBatch(db)

            // eslint-disable-next-line
            for (const document of translations.docs) {
              const translationsDocRef = doc(newTranslationsCollection, document.id)

              batch.set(translationsDocRef, document.data())
            }

            await batch.commit()
          }

          store.dispatch('revisions/create', {
            event: RevisionEventType.DUPLICATE_PAGES,
            id: newDocRef.id,
            newValue: usesCloudStorageBackend ? { ...data, recipes: null } : data,
            previousValue: {},
          })

          router.push({
            name: 'pages-page-settings',
            params: { id: newDocRef.id },
          })

          store.dispatch('notify', {
            title: i18n.t('Great!'),
            body: i18n.t('A new {title} has been duplicated 🚀', { title: i18n.t('page') }),
          })

          resolve(newDocRef)
        } catch (error) {
          store.dispatch('notify', {
            body: i18n.t('Something went wrong duplicating the {title}', { title: i18n.t('page') }),
            variant: 'danger',
          })

          reject(error)
        }
      })
    },

    /**
     * Fetch all pages which correspond to having a specific Form Id
     *
     * @param {Object}
     * @param {string} id Id of the form
     *
     * @returns {Promise}
     */
    fetchAllByFormId({ commit }, id) {
      return new Promise((resolve, reject) => {
        const { pages } = tenantCtx()

        const q = query(pages, where('formId', '==', id), orderBy('createdAt', 'desc'))

        getDocs(q)
          .then(querySnapshot => {
            commit('UPDATE_LIST_RELATED', querySnapshot.docs)
            resolve(querySnapshot.docs)
          })
          .catch(error => reject(error))
      })
    },

    /**
     * Reads the saved, rendered HTML and CSS of a page without touching the
     * current page state. Cloud Storage assets are preferred for the
     * `google-cloud-storage` backend, Firestore recipes otherwise, with the
     * other source as fallback.
     *
     * @param {Object} _ctx
     * @param {string} id - The page ID
     *
     * @returns {Promise<{ html: string, css: string, source: 'storage'|'firestore'|null }>}
     */
    async fetchPageRender(_ctx, id) {
      const { pages, pagesStorage } = tenantCtx()

      await store.dispatch('tenant/fetchFeatureFlags')

      const featureFlags = store.getters['tenant/getFeatureFlags'] || {}
      const prefersStorage = featureFlags.PAGE_STORAGE_BACKEND === 'google-cloud-storage'

      const readStorageText = async fileName => {
        try {
          const url = await getDownloadURL(storageRef(storageRef(pagesStorage, id), fileName))
          const response = await fetch(url)

          if (!response.ok) return null

          return await response.text()
        } catch (error) {
          if (error?.code !== 'storage/object-not-found') console.debug(error)

          return null
        }
      }

      const fromStorage = async () => {
        if (!pagesStorage) return null

        const [html, css] = await Promise.all([
          readStorageText('index.html'),
          readStorageText('styles.css'),
        ])

        return html == null ? null : { html, css: css || '', source: 'storage' }
      }

      const fromFirestore = async () => {
        const snapshot = await getDoc(doc(pages, id))
        const recipes = snapshot.data()?.recipes || {}
        const html = recipes['novti-recipe-html']

        return html == null ? null : { html, css: recipes['novti-recipe-css'] || '', source: 'firestore' }
      }

      const readers = prefersStorage ? [fromStorage, fromFirestore] : [fromFirestore, fromStorage]

      // eslint-disable-next-line no-restricted-syntax
      for (const read of readers) {
        // eslint-disable-next-line no-await-in-loop
        const result = await read()

        if (result) return result
      }

      return { html: '', css: '', source: null }
    },
  },
}

/**
 * Handles the revision for a page Update.
 * It merges all the changes from the payload with the the state of the selected page.
 * Different then with other revisions the recipes are deleted from the previousValue variable because of file size constrains
 *
 * @param {Object} state
 * @param {Object} payload
 * @param {String} id
 *
 * @returns {Promise}
 */
const handlePageUpdateRevision = (state, payload, id) => {
  store.dispatch('revisions/create', {
    event: RevisionEventType.UPDATE_PAGES,
    id,
    newValue: {
      ...payload,
    },
    previousValue: { ...state.current.data, recipes: null },
  })
}
